/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { fetchSeverityDistribution } from 'services/hvarisk.service';
import { useAppContext } from 'providers/AppProvider';

echarts.use([
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  CanvasRenderer
]);

interface RiskSeverityChartProps {
  dateRange?: {
    datefrom: string;
    dateto: string;
  };
  campaignId?: number;
  categoryId?: number;
  serviceId?: number;
}

const RiskSeverityChart: React.FC<RiskSeverityChartProps> = ({
  dateRange,
  campaignId,
  categoryId,
  serviceId
}) => {
  const {
    config: { isDark }
  } = useAppContext();
  const [chartData, setChartData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalRisks, setTotalRisks] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const params = {
          datefrom:
            dateRange?.datefrom ||
            new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
              .toISOString()
              .split('T')[0],
          dateto: dateRange?.dateto || new Date().toISOString().split('T')[0],
          campaignid: campaignId,
          categoryid: categoryId,
          serviceid: serviceId
        };

        const distribution = await fetchSeverityDistribution(params);

        const chartData = distribution.map(item => ({
          value: item.count,
          name: item.severityLabel,
          severity: item.severity,
          percentage: item.percentage,
          itemStyle: {
            color: getSeverityColor(item.severity)
          }
        }));

        setChartData(chartData);
        setTotalRisks(distribution.reduce((sum, item) => sum + item.count, 0));
      } catch (error) {
        console.error('Error fetching severity distribution:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dateRange, campaignId, categoryId, serviceId]);

  const getSeverityColor = (severity: number) => {
    const colors = {
      5: '#c00000', // Critical - red
      4: '#fd7e14', // High - orange
      3: '#ffc107', // Medium - yellow
      2: '#28a745', // Low - green
      1: '#38b755' // Very Low - gray
    };
    return colors[severity as keyof typeof colors] || '#6c757d';
  };

  const option = {
    tooltip: {
      trigger: 'item',
      backgroundColor: !isDark
        ? 'rgba(240, 240, 240, 0.9)'
        : 'rgba(49, 55, 74, 0.9)',
      textStyle: {
        color: !isDark ? '#202020' : '#ccc',
        fontFamily: 'Nunito Sans',
        fontSize: 14
      },
      borderWidth: 1,
      borderColor: !isDark
        ? 'rgba(49, 55, 74, 0.4)'
        : 'rgba(140, 140, 180, 0.8)',
      padding: 10,
      formatter: (params: any) => {
        return `
          <div style="font-weight: bold; margin-bottom: 4px;">
            ${params.name} (Level ${params.data.severity})
          </div>
          <div style="display: flex; justify-content: space-between; gap: 20px;">
            <span>Count:</span>
            <span style="font-weight: bold;">${params.value}</span>
          </div>
          <div style="display: flex; justify-content: space-between; gap: 20px;">
            <span>Percentage:</span>
            <span style="font-weight: bold;">${params.percent}%</span>
          </div>
        `;
      }
    },
    series: [
      {
        name: 'Risk Severity',
        type: 'pie',
        top: 30,
        radius: ['40%', '70%'],
        center: ['50%', '45%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: !isDark ? '#fff' : '#aaa',
          borderWidth: 1
        },
        label: {
          show: true,
          position: 'outside',
          formatter: '{b}: {c}',
          color: !isDark ? '#333' : '#ddd'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: '14',
            fontWeight: 'bold'
          },
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        },
        data: chartData
      }
    ]
  };

  return (
    <div className="h-100">
      <div className="text-center">
        <h5 className="mb-0 mt-2">Risk Distribution by Severity</h5>
        <small className="text-muted">Total: {totalRisks} risks analyzed</small>
      </div>
      <div>
        {isLoading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: '350px' }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <ReactEChartsCore
            echarts={echarts}
            option={option}
            style={{ height: '350px' }}
            notMerge={true}
            lazyUpdate={true}
          />
        )}
      </div>
    </div>
  );
};

export default RiskSeverityChart;
